<script lang="ts">
  import { createEventDispatcher } from 'svelte';
  
  export let startDate: string = '';
  export let endDate: string = '';
  export let placeholder: string = 'Date Range';
  export let fullWidth: boolean = false;

  const dispatch = createEventDispatcher();
  let isOpen = false;
  let dateRangeRef: HTMLDivElement;

  // Format date for display
  function formatDateForDisplay(date: string): string {
    if (!date) return '';
    try {
      const dateObj = new Date(date + 'T00:00:00'); // Add time to avoid timezone issues
      return dateObj.toLocaleDateString('en-US', { 
        month: 'short', 
        day: 'numeric', 
        year: 'numeric' 
      });
    } catch (error) {
      console.error('Date formatting error:', error);
      return date;
    }
  }

  // Get display text for the date range
  function getDisplayText(): string {
    if (!startDate && !endDate) return placeholder;
    if (startDate && endDate) {
      return `${formatDateForDisplay(startDate)} - ${formatDateForDisplay(endDate)}`;
    }
    if (startDate) {
      return `From ${formatDateForDisplay(startDate)}`;
    }
    if (endDate) {
      return `Until ${formatDateForDisplay(endDate)}`;
    }
    return placeholder;
  }

  function toggleDropdown() {
    isOpen = !isOpen;
  }

  function closeDropdown() {
    isOpen = false;
  }

  function handleClickOutside(event: MouseEvent) {
    if (dateRangeRef && !dateRangeRef.contains(event.target as Node)) {
      closeDropdown();
    }
  }

  function clearDates() {
    console.log('Clear clicked - before:', { startDate, endDate }); // Debug log
    startDate = '';
    endDate = '';
    console.log('Clear clicked - after:', { startDate, endDate }); // Debug log
    // Force reactive update
    displayText = getDisplayText();
    dispatch('change', { startDate, endDate });
    closeDropdown();
  }

  function applyDates() {
    console.log('Apply clicked:', { startDate, endDate }); // Debug log
    dispatch('apply', { startDate, endDate }); // Dispatch specific apply event
    dispatch('change', { startDate, endDate }); // Keep original event for compatibility
    // Force reactive update
    displayText = getDisplayText();
    closeDropdown();
  }

  // Handle date input changes
  function handleStartDateChange(event: Event) {
    const target = event.target as HTMLInputElement;
    startDate = target.value;
    // Don't dispatch change event here, only when Apply is clicked
  }

  function handleEndDateChange(event: Event) {
    const target = event.target as HTMLInputElement;
    endDate = target.value;
    // Don't dispatch change event here, only when Apply is clicked
  }

  // Handle escape key
  function handleKeydown(event: KeyboardEvent) {
    if (event.key === 'Escape') {
      closeDropdown();
    }
  }

  // Close dropdown when clicking outside
  $: if (typeof window !== 'undefined') {
    if (isOpen) {
      document.addEventListener('click', handleClickOutside);
      document.addEventListener('keydown', handleKeydown);
    } else {
      document.removeEventListener('click', handleClickOutside);
      document.removeEventListener('keydown', handleKeydown);
    }
  }

  // Reactive display text - explicit dependencies
  $: displayText = getDisplayText();
  
  // Force update when dates change
  $: if (startDate || endDate) {
    displayText = getDisplayText();
  }
</script>

<div class="date-range-wrapper" class:full-width={fullWidth} bind:this={dateRangeRef}>
  <button type="button" class="date-range-trigger" class:open={isOpen} on:click={toggleDropdown}>
    <span class="date-range-text">{displayText}</span>
    <svg class="calendar-icon" width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
      <path d="M19 3h-1V1h-2v2H8V1H6v2H5c-1.11 0-1.99.9-1.99 2L3 19c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V8h14v11zM7 10h5v5H7z"/>
    </svg>
  </button>

  {#if isOpen}
    <div class="date-range-dropdown">
      <div class="dropdown-header">
        <h4 class="dropdown-title">Select Date Range</h4>
        <button type="button" class="close-button" on:click={closeDropdown}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
            <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
          </svg>
        </button>
      </div>
      
      <div class="date-inputs">
        <div class="date-input-group">
          <label class="date-label" for="date-range-start">Start Date</label>
          <input
            id="date-range-start"
            type="date"
            class="date-input"
            bind:value={startDate}
            max={endDate || undefined}
            on:change={handleStartDateChange}
          />
        </div>
        
        <div class="date-input-group">
          <label class="date-label" for="date-range-end">End Date</label>
          <input
            id="date-range-end"
            type="date"
            class="date-input"
            bind:value={endDate}
            min={startDate || undefined}
            on:change={handleEndDateChange}
          />
        </div>
      </div>
      
      <div class="dropdown-footer">
        <button type="button" class="clear-button" on:click={clearDates}>
          Clear
        </button>
        <button type="button" class="apply-button" on:click={applyDates}>
          Apply
        </button>
      </div>
    </div>
  {/if}
</div>

<style>
  .date-range-wrapper {
    position: relative;
    display: inline-block;
  }

  .full-width {
    width: 100%;
  }

  .date-range-trigger {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 200px;
    padding: 0.75rem 1rem;
    border: 1px solid #d1d5db;
    border-radius: 50rem;
    background: white;
    font-size: 0.875rem;
    color: #6b7280;
    cursor: pointer;
    transition: border-color 0.2s, box-shadow 0.2s;
  }

  .full-width .date-range-trigger {
    width: 100%;
  }

  .date-range-trigger:hover {
    border-color: #9ca3af;
  }

  .date-range-trigger:focus,
  .date-range-trigger.open {
    outline: none;
    border-color: #f0b429;
    box-shadow: 0 0 0 3px rgba(240, 180, 41, 0.1);
  }

  .date-range-text {
    flex: 1;
    text-align: left;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .calendar-icon {
    margin-left: 0.5rem;
    color: #9ca3af;
    flex-shrink: 0;
  }

  .date-range-dropdown {
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    background: white;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
    z-index: 1000;
    margin-top: 0.25rem;
    min-width: 280px;
  }

  .dropdown-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 1rem;
    border-bottom: 1px solid #e5e7eb;
  }

  .dropdown-title {
    font-size: 0.875rem;
    font-weight: 600;
    color: #111827;
    margin: 0;
  }

  .close-button {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    border: none;
    background: none;
    color: #6b7280;
    cursor: pointer;
    border-radius: 4px;
    transition: background-color 0.2s;
  }

  .close-button:hover {
    background-color: #f3f4f6;
    color: #374151;
  }

  .date-inputs {
    padding: 1rem;
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }

  .date-input-group {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }

  .date-label {
    font-size: 0.75rem;
    font-weight: 500;
    color: #374151;
    text-transform: uppercase;
    letter-spacing: 0.05em;
  }

  .date-input {
    width: 100%;
    padding: 0.75rem;
    border: 1px solid #d1d5db;
    border-radius: 6px;
    font-size: 0.875rem;
    color: #111827;
    transition: border-color 0.2s, box-shadow 0.2s;
  }

  .date-input:focus {
    outline: none;
    border-color: #f0b429;
    box-shadow: 0 0 0 3px rgba(240, 180, 41, 0.1);
  }

  .dropdown-footer {
    display: flex;
    justify-content: space-between;
    padding: 1rem;
    border-top: 1px solid #e5e7eb;
    gap: 0.5rem;
  }

  .clear-button {
    padding: 0.5rem 1rem;
    border: 1px solid #d1d5db;
    border-radius: 6px;
    background: white;
    color: #6b7280;
    font-size: 0.875rem;
    cursor: pointer;
    transition: all 0.2s;
  }

  .clear-button:hover {
    background-color: #f9fafb;
    border-color: #9ca3af;
  }

  .apply-button {
    padding: 0.5rem 1rem;
    border: none;
    border-radius: 6px;
    background: #f0b429;
    color: white;
    font-size: 0.875rem;
    font-weight: 500;
    cursor: pointer;
    transition: background-color 0.2s;
  }

  .apply-button:hover {
    background: #e5a41f;
  }

  /* Responsive adjustments */
  @media (max-width: 768px) {
    .date-range-dropdown {
      min-width: 260px;
    }
    
    .date-inputs {
      padding: 0.75rem;
    }
    
    .dropdown-footer {
      padding: 0.75rem;
    }
  }
</style> 